import { navigationUrl, type VisitDto } from '@kominiarz/shared';
import { ChevronLeft, ChevronRight, MapPinOff, Navigation } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useCurrentUser } from '../../auth/useAuth';
import { Alert } from '../../components/Alert';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Spinner } from '../../components/Loading';
import { MarkersMap, type MapMarker } from '../../components/map';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { VisitStatusBadge, visitClientName, visitColor, visitTimeRange, visitTitle } from '../../components/VisitStatus';
import { errorMessage } from '../../lib/api';
import { capitalize, local, shiftDate, timeRange, todayLocal, viewRange } from '../../lib/calendarDates';
import { readPref } from '../../lib/storage';
import { useUserOptions } from '../../lib/usersApi';
import { useVisits } from '../../lib/visitsApi';
import styles from './map.module.css';

const isDate = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v) && local(v).isValid;

/** The day's visits on a map, numbered in order of time, pins in the assignee's colour. */
export function DayMapPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const users = useUserOptions();
  const date = isDate(params.get('data')) ? params.get('data')! : todayLocal();
  const filter = params.get('filtr') ?? readPref('calendar.filter') ?? 'all';
  const range = useMemo(() => viewRange('dzien', date), [date]);
  const visits = useVisits(range, filter);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  // Cancelled visits are not on the route; the rest are numbered by start time.
  const active = (visits.data ?? []).filter((v) => v.status !== 'cancelled');
  const numbered = active.map((v, i) => ({ visit: v, number: i + 1 }));
  const markers: MapMarker[] = numbered
    .filter(({ visit: v }) => v.lat != null && v.lng != null)
    .map(({ visit: v, number }) => ({
      id: v.id,
      lat: v.lat!,
      lng: v.lng!,
      label: String(number),
      color: visitColor(v),
      done: v.status === 'done',
      title: `${number}. ${visitTimeRange(v)} ${visitTitle(v)}${v.address ? `, ${v.address}` : ''}`,
      onClick: () => navigate(`/wizyty/${v.id}`),
    }));
  const withoutPin = numbered.filter(({ visit: v }) => v.lat == null || v.lng == null);

  const filterOptions = [
    { value: 'all', label: 'Wszystkie' },
    { value: 'mine', label: 'Moje' },
    ...(user.role === 'admin'
      ? (users.data ?? []).filter((u) => u.id !== user.id).map((u) => ({ value: `user:${u.id}`, label: u.name }))
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Mapa dnia"
        actions={
          <Button variant="secondary" onClick={() => update({ data: null })} aria-pressed={date === todayLocal()}>
            Dziś
          </Button>
        }
      />
      <PageBody>
        <div className={styles.toolbar}>
          <Button
            variant="secondary"
            className={styles.arrow}
            icon={<ChevronLeft aria-hidden="true" />}
            aria-label="Poprzedni dzień"
            onClick={() => update({ data: shiftDate('dzien', date, -1) })}
          >
            <span className="visually-hidden">Wstecz</span>
          </Button>
          <h2 className={styles.title} aria-live="polite">
            {capitalize(local(date).toFormat('EEEE, d MMMM'))}
          </h2>
          <Button
            variant="secondary"
            className={styles.arrow}
            icon={<ChevronRight aria-hidden="true" />}
            aria-label="Następny dzień"
            onClick={() => update({ data: shiftDate('dzien', date, 1) })}
          >
            <span className="visually-hidden">Dalej</span>
          </Button>
          <label className={styles.filter}>
            <span className="visually-hidden">Czyje wizyty pokazać</span>
            <select value={filter} onChange={(e) => update({ filtr: e.target.value })}>
              {filterOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {visits.isError && <Alert kind="error">{errorMessage(visits.error)}</Alert>}
        {visits.isPending ? (
          <Spinner />
        ) : active.length === 0 ? (
          <EmptyState icon={MapPinOff} title="Brak wizyt tego dnia">
            Wybierz inny dzień strzałkami.
          </EmptyState>
        ) : (
          <div className={styles.layout}>
            <MarkersMap markers={markers} height="min(58dvh, 34rem)" />
            <ol className={styles.list}>
              {numbered.map(({ visit: v, number }) => (
                <RouteItem key={v.id} visit={v} number={number} />
              ))}
            </ol>
          </div>
        )}
        {withoutPin.length > 0 && (
          <Alert kind="info" title="Nie wszystkie wizyty są na mapie">
            {withoutPin.length === 1 ? 'Wizyta nr ' : 'Wizyty nr '}
            {withoutPin.map((w) => w.number).join(', ')} nie mają pinezki. Otwórz wizytę i użyj „Znajdź na mapie”.
          </Alert>
        )}
      </PageBody>
    </>
  );
}

function RouteItem({ visit: v, number }: { visit: VisitDto; number: number }) {
  const user = useCurrentUser();
  const nav = navigationUrl(user.settings.navApp, v);
  const client = visitClientName(v);
  return (
    <li className={styles.item}>
      <span
        className={styles.number}
        style={{ background: `var(--user-${visitColor(v)})`, color: `var(--user-${visitColor(v)}-ink)` }}
        aria-hidden="true"
      >
        {number}
      </span>
      <Link to={`/wizyty/${v.id}`} className={styles.itemLink}>
        <span className="visually-hidden">Wizyta {number}: </span>
        <span className={styles.itemTop}>
          <span className={styles.time}>{visitTimeRange(v)}</span>
          <span>{visitTitle(v)}</span>
          <VisitStatusBadge status={v.status} />
        </span>
        {client && <span className={styles.itemClient}>{client}</span>}
        {v.address && <span className={styles.itemAddress}>{v.address}</span>}
        {(v.lat == null || v.lng == null) && <span className={styles.noPin}>Bez pinezki</span>}
      </Link>
      {nav && (
        <a className={styles.nav} href={nav} target="_blank" rel="noopener noreferrer">
          <Navigation aria-hidden="true" />
          <span>Nawiguj</span>
        </a>
      )}
    </li>
  );
}
